<template>
  <div class="contenedor-principal">
    <!-- Ventana central -->
    <div class="ventana-central">
      <!-- Botón de regresar -->
      <button class="btn-regresar" @click="regresar">
        <span class="material-icons">arrow_back</span>
      </button>

      <!-- Logotipo en la esquina superior derecha -->
      <img 
        class="logo" 
        :src="logotipo" 
        alt="Logotipo"
      />

      <!-- Título -->
      <h2 class="titulo">Consulta el folio de tu expediente médico</h2>

      <!-- Barra de búsqueda -->
      <div class="barra-busqueda">
        <input 
          v-model="folio" 
          type="text" 
          class="form-control" 
          placeholder="Introduce tu folio..." 
          @keyup.enter="buscarFolio"
        >
        <button class="btn-buscar" @click="buscarFolio">
          <span class="material-icons">search</span>
        </button>
      </div>

      <!-- Enlaces adicionales -->
      <div class="enlaces">
        <a href="#" @click.prevent="mostrarModalPoliticas">Ver políticas de la empresa</a>
      </div>
    </div>

    <!-- Modal: No conozco mi folio -->
    <transition name="fade">
      <div class="modal fade show" tabindex="-1" role="dialog" v-if="showModalFolio" style="display: block; background: rgba(0,0,0,0.5);" @click.self="cerrarModalFolio">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">No conozco mi folio</h5>
              <button type="button" class="close" @click="cerrarModalFolio" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body text-center">
              <img :src="imagenFolio" alt="No conozco mi folio" class="img-fluid">
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="cerrarModalFolio">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal: Políticas de la empresa -->
    <transition name="fade">
      <div class="modal fade show" tabindex="-1" role="dialog" v-if="showModalPoliticas" style="display: block; background: rgba(0,0,0,0.5);" @click.self="cerrarModalPoliticas">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Políticas de la Empresa</h5>
              <button type="button" class="close" @click="cerrarModalPoliticas" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <ul>
                <li>Política de confidencialidad de la información del paciente.</li>
                <li>Política de higiene y seguridad en la atención médica.</li>
                <li>Política de protección de datos y privacidad.</li>
                <li>Política de atención y servicio al paciente.</li>
                <li>Política de responsabilidad y ética médica.</li>
              </ul>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="cerrarModalPoliticas">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';
import { folioExiste } from '@/utils/api';

// IMPORTA el logotipo (ajusta según la configuración de tu proyecto).
import logotipo from '@/assets/imagenes/logotipo.png';
// O bien: const logotipo = require('@/assets/imagenes/logotipo.png');

export default {
  name: 'BusquedaPacientes',
  data() {
    return {
      folio: '',
      showModalFolio: false,
      showModalPoliticas: false,
      imagenFolio: '/buscar_folio.png',
      logotipo // Agregamos la referencia al logotipo
    };
  },
  methods: {
    regresar() {
      this.$router.push('/');
    },
    async buscarFolio() {
      let folioLimpio = DOMPurify.sanitize(this.folio.trim())
        .split('')
        .filter(c => c.charCodeAt(0) > 0x1F && c.charCodeAt(0) !== 0x7F)
        .join('');

      if (!folioLimpio || folioLimpio.length > 50) {
        Swal.fire({
          icon: 'error',
          title: 'Folio inválido',
          text: 'Ingresa un folio válido.',
          confirmButtonColor: '#d33'
        });
        return;
      }

      if (await folioExiste(folioLimpio)) {
        this.$router.push(`/info/${folioLimpio}`);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Folio no encontrado',
          text: `El folio ${folioLimpio} no está registrado en el sistema.`,
          confirmButtonColor: '#d33'
        });
      }
    },
    mostrarModalFolio() {
      this.showModalFolio = true;
    },
    cerrarModalFolio() {
      this.showModalFolio = false;
    },
    mostrarModalPoliticas() {
      this.showModalPoliticas = true;
    },
    cerrarModalPoliticas() {
      this.showModalPoliticas = false;
    }
  }
};
</script>

<style scoped>
/* ============================================================
   DOMÉDICA - ESTILOS PARA PANTALLA DE BÚSQUEDA (CON BARRA, LOGO, ENLACES Y MODALES)
   Basado en el Manual de Identidad (PDF)
   ============================================================ */

/* ---------- ANIMACIÓN DE ENTRADA ---------- */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ---------- CONTENEDOR PRINCIPAL (60% Dominante - Azul Profundo) ---------- */
.contenedor-principal {
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: slideIn 0.5s ease forwards;
  background-color: var(--brand-primary); /* Azul profundo #133E68 */
}

/* ---------- VENTANA CENTRAL / TARJETA (Fondo Beige) ---------- */
.ventana-central {
  background: var(--brand-beige);           /* Beige #F4EFEB */
  padding: 50px;
  border-radius: 20px;
  width: 70%;
  height: 80%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  box-shadow: 0px 10px 30px rgba(19, 62, 104, 0.25); /* Sombra con tono de marca */
}

/* ---------- BOTÓN DE REGRESO (Acento secundario - Azul Profundo) ---------- */
.btn-regresar {
  position: absolute;
  top: 15px;
  left: 20px;
  background: none;
  border: none;
  color: var(--brand-primary);              /* Azul profundo */
  cursor: pointer;
  transition: transform 0.3s ease, color 0.3s ease;
}

.btn-regresar:hover {
  transform: scale(1.1);
  color: var(--brand-accent-sage);          /* Verde Salvia al hacer hover */
}

.btn-regresar .material-icons {
  font-size: 38px;
}

/* ---------- LOGO (posicionado a la derecha) ---------- */
.logo {
  position: absolute;
  top: 15px;
  right: 20px;
  width: 150px; /* Ajusta a tu gusto */
  height: auto;
}

/* ---------- TÍTULO (30% Secundario - Gris Oscuro) ---------- */
.titulo {
  color: var(--brand-secondary);            /* Gris oscuro #575756 */
  font-size: 26px;
  margin-bottom: 30px;
  font-weight: 700;
  text-align: center;
}

/* ---------- BARRA DE BÚSQUEDA ---------- */
.barra-busqueda {
  display: flex;
  width: 50%;
  align-items: center;
  background: var(--white);
  border-radius: 25px;
  border: 2px solid var(--brand-secondary-light); /* Gris claro #999999 */
  overflow: hidden;
  padding: 5px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

/* Efecto focus en el contenedor de la barra */
.barra-busqueda:focus-within {
  border-color: var(--brand-accent-sage);    /* Verde Salvia al enfocar */
  box-shadow: 0 0 0 4px rgba(96, 127, 112, 0.2);
}

/* Campo de entrada */
.barra-busqueda input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 500;
  color: var(--brand-secondary);
  background: transparent;
}

.barra-busqueda input::placeholder {
  color: var(--brand-secondary-light);
  font-weight: 400;
}

/* ---------- BOTÓN DE BÚSQUEDA (10% Acento - Verde Salvia) ---------- */
.btn-buscar {
  background: var(--brand-accent-sage);     /* Verde Salvia #607F70 */
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
  margin-right: 4px;
}

.btn-buscar .material-icons {
  color: var(--white);
  font-size: 22px;
}

.btn-buscar:hover {
  background-color: var(--brand-accent-sage-dark); /* #4A6255 (15% más oscuro) */
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(96, 127, 112, 0.4);
}

.btn-buscar:active {
  transform: scale(0.94);
}

/* ---------- ENLACES INFERIORES ---------- */
.enlaces {
  margin-top: 20px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
}

.enlaces a {
  color: var(--brand-primary);              /* Azul profundo para enlaces */
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: color 0.3s ease, text-decoration 0.3s ease;
}

.enlaces a:hover {
  color: var(--brand-accent-sage);          /* Verde Salvia en hover */
  text-decoration: underline;
}

/* ---------- MODALES (adaptados a la marca) ---------- */

/* Fondo del modal (overlay) */
.modal {
  overflow: auto;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
  background-color: rgba(19, 62, 104, 0.5); /* Overlay con Azul profundo */
}

.modal-dialog {
  margin: 1.75rem auto;
  max-width: 500px;
}

.modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(19, 62, 104, 0.3);
  background-color: var(--white);
  overflow: hidden;
}

/* Cabecera del modal (60% Dominante - Azul Profundo) */
.modal-header {
  background-color: var(--brand-primary);
  color: var(--white);
  padding: 18px 24px;
  border-bottom: 3px solid var(--brand-accent-sage); /* Detalle en Salvia */
}

.modal-header .modal-title {
  font-weight: 700;
  color: var(--white);
}

/* Botón cerrar (X) del modal */
.close {
  background: none;
  border: none;
  font-size: 1.8rem;
  color: var(--white);
  opacity: 0.8;
  transition: opacity 0.3s ease, transform 0.2s ease;
}

.close:hover {
  opacity: 1;
  transform: scale(1.1);
}

/* Cuerpo del modal */
.modal-body {
  padding: 24px;
  color: var(--brand-secondary);
  font-weight: 500;
}

/* Pie del modal (si lo usas) */
.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--brand-secondary-light);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Botones dentro del modal (usando la paleta) */
.modal-footer .btn {
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.modal-footer .btn-primary {
  background-color: var(--brand-accent-sage);
  color: var(--white);
}

.modal-footer .btn-primary:hover {
  background-color: var(--brand-accent-sage-dark);
  transform: translateY(-2px);
}

.modal-footer .btn-secondary {
  background-color: var(--brand-secondary-light);
  color: var(--white);
}

.modal-footer .btn-secondary:hover {
  background-color: var(--brand-secondary);
}

/* ---------- RESPONSIVE PARA MÓVILES ---------- */
@media (max-width: 768px) {
  .ventana-central {
    width: 95%;
    height: auto;
    min-height: 80vh;
    padding: 30px 20px;
  }

  .logo {
    width: 100px;
    top: 10px;
    right: 10px;
  }

  .btn-regresar {
    top: 10px;
    left: 10px;
  }

  .btn-regresar .material-icons {
    font-size: 30px;
  }

  .titulo {
    font-size: 20px;
    margin-top: 30px;
  }

  .barra-busqueda {
    width: 90%;
    padding: 3px;
  }

  .barra-busqueda input {
    font-size: 14px;
    padding: 10px 12px;
  }

  .btn-buscar {
    padding: 8px 12px;
  }

  .btn-buscar .material-icons {
    font-size: 18px;
  }

  .enlaces {
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }

  .modal-dialog {
    margin: 0.5rem auto;
    max-width: 95%;
  }
}
</style>
